import React, { useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import GlassModal from './Common/GlassModal';

// Cancellation reasons grouped by category
export const cancellationReasons = {
  'Customer-Initiated': [
    { value: 'customer_budget', label: 'Budget Constraints' },
    { value: 'customer_timing', label: 'Wants to Wait/Delay' },
    { value: 'customer_found_other', label: 'Found Another Provider' },
    { value: 'customer_no_response', label: 'No Response/Ghosted' },
    { value: 'customer_changed_mind', label: 'Changed Mind/No Longer Needed' },
    { value: 'customer_dissatisfied', label: 'Dissatisfied with Quote/Proposal' }
  ],
  'Company-Initiated': [
    { value: 'company_capacity', label: 'At Capacity/Too Busy' },
    { value: 'company_outside_area', label: 'Outside Service Area' },
    { value: 'company_not_qualified', label: 'Lack Expertise/Equipment' },
    { value: 'company_unprofitable', label: 'Not Profitable Enough' }
  ],
  'Mutual/External': [
    { value: 'pricing_disagreement', label: 'Pricing Disagreement' },
    { value: 'scheduling_conflict', label: 'Scheduling Conflict' },
    { value: 'scope_changed', label: 'Scope Changed Significantly' },
    { value: 'duplicate', label: 'Duplicate Work Order' },
    { value: 'entered_in_error', label: 'Entered in Error' },
    { value: 'weather', label: 'Weather Prevented Work' },
    { value: 'emergency', label: 'Emergency Cancellation' },
    { value: 'permit_issues', label: 'Permit/Regulatory Issues' },
    { value: 'property_sold', label: 'Property Sold/Changed Ownership' },
    { value: 'other', label: 'Other (Explain in Notes)' }
  ]
};

// #6103: the saved reason is a value; screens that show it need the label the user picked.
export const cancellationReasonLabel = (value) => {
  if (!value) return null;
  for (const reasons of Object.values(cancellationReasons)) {
    const match = reasons.find(r => r.value === value);
    if (match) return match.label;
  }
  return String(value).replace(/_/g, ' ');
};

/**
 * Cancellation Modal Component
 * Competitive Advantage: Better tracking than ServiceTitan/Jobber/Housecall Pro
 * - 20 cancellation reasons (vs their 5-7)
 * - Track who initiated (customer vs company)
 * - Required notes for "other" reason
 * - Better metrics and reporting
 */
const CancellationModal = ({ isOpen, onClose, onConfirm, jobTitle = 'this job', workOrderNumber = null }) => {
  const [formData, setFormData] = useState({
    initiatedBy: 'customer',
    reason: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.reason) {
      newErrors.reason = 'Please select a cancellation reason';
    }

    // Require notes if reason is "other"
    if (formData.reason === 'other' && !formData.notes.trim()) {
      newErrors.notes = 'Notes are required when selecting "Other"';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onConfirm(formData);
      // Reset form
      setFormData({
        initiatedBy: 'customer',
        reason: '',
        notes: ''
      });
      setErrors({});
    }
  };

  const handleClose = () => {
    // Reset form on close
    setFormData({
      initiatedBy: 'customer',
      reason: '',
      notes: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="max-w-2xl"
      closeOnOverlay={true}
      header={(
        <>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Cancel Job</h3>
              <p className="text-sm text-gray-400">Provide cancellation details for tracking</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white transition-colors">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </>
      )}
      footer={(
        <>
          {/* Unambiguous labels: the safe action says it KEEPS the job; the destructive action
              names the irreversible outcome. Keep the colors (neutral = safe, red = destructive). */}
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-300 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-colors"
          >
            No, keep it
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
          >
            Yes, cancel this job
          </button>
        </>
      )}
    >
      {/* Body */}
      <div className="space-y-6">
        {/* Job Title */}
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <p className="text-sm text-gray-400">Cancelling:</p>
          {/* #6102: the number tells two same-titled jobs apart before the irreversible step. */}
          <p className="text-base font-semibold text-white">{workOrderNumber ? `${workOrderNumber} · ` : ''}{jobTitle}</p>
        </div>

        {/* Who Initiated */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Who is initiating the cancellation? <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: 'customer', label: 'Customer', icon: '👤' },
              { value: 'company', label: 'Company', icon: '🏢' },
              { value: 'mutual', label: 'Mutual', icon: '🤝' }
            ].map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleChange('initiatedBy', option.value)}
                className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                  formData.initiatedBy === option.value
                    ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                    : 'border-white/20 bg-white/5 text-gray-300 hover:border-white/40'
                }`}
              >
                <span className="text-2xl mb-2">{option.icon}</span>
                <span className="text-sm font-medium">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Cancellation Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Cancellation Reason <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.reason}
            onChange={(e) => handleChange('reason', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.reason ? 'border-red-500' : 'border-white/20'
            } bg-white/10 text-gray-200`}
          >
            <option value="">Select a reason...</option>
            {Object.entries(cancellationReasons).map(([category, reasons]) => (
              <optgroup key={category} label={category}>
                {reasons.map(reason => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {errors.reason && (
            <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Additional Notes {formData.reason === 'other' && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={4}
            placeholder="Provide additional details about the cancellation..."
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
              errors.notes ? 'border-red-500' : 'border-white/20'
            } bg-white/10 text-white placeholder-gray-500`}
          />
          {errors.notes && (
            <p className="mt-1 text-sm text-red-600">{errors.notes}</p>
          )}
          <p className="mt-1 text-xs text-gray-400">
            These notes will help track patterns and improve future estimates
          </p>
        </div>

        {/* Warning */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="ml-3">
              <h4 className="text-sm font-medium text-yellow-300">This action cannot be undone</h4>
              <p className="mt-1 text-sm text-yellow-400">
                The job will be marked as cancelled. You can find it under Jobs › Cancelled.
              </p>
            </div>
          </div>
        </div>
      </div>
    </GlassModal>
  );
};

export default CancellationModal;

